import { useEffect, useRef, useState } from "react";
import { MapPinOff } from "lucide-react";

import { loadAmap } from "@/shared/lib/amap/amapLoader";
import { getBoundary, type DistrictNode } from "@/shared/lib/amap/districtService";
import type { MapLeadItem } from "../../api/mapLeadService";

/* 高德 JS API 无官方 TS 类型,本文件收口所有 any 交互,组件外零渗漏。 */
/* eslint-disable @typescript-eslint/no-explicit-any */

interface Props {
  items: MapLeadItem[];
  selectedPoiId: string | null;
  onSelect: (poiId: string) => void;
  center: { lng: number; lat: number } | null;
  onPickCenter: (c: { lng: number; lat: number }) => void;
  radiusMeters: number;
  aroundMode: boolean;
  onReady: (ready: boolean) => void;
  /** 行政区联动焦点(区县优先):选中即描边界 + setBounds;null 不动地图。 */
  focus: DistrictNode | null;
}

/**
 * 高德地图区。Key 未配置/加载失败 → 占位说明(搜索与列表不受影响,V1.1 打桩开发约定)。
 * Marker:普通=蓝点,高分(≥80)=橙点,选中=放大描边;周边模式画半径圈,点图设中心。
 */
export function LeadMap({
  items,
  selectedPoiId,
  onSelect,
  center,
  onPickCenter,
  radiusMeters,
  aroundMode,
  onReady,
  focus,
}: Props) {
  const holder = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const amapRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const circleRef = useRef<any>(null);
  const boundaryRef = useRef<any[]>([]);
  const centerMarkerRef = useRef<any>(null);
  const [state, setState] = useState<"loading" | "ready" | "unavailable">("loading");

  // 装载 + 建图(一次)。
  useEffect(() => {
    let disposed = false;
    loadAmap().then((AMap: any) => {
      if (disposed) return;
      if (!AMap || !holder.current) {
        setState("unavailable");
        onReady(false);
        return;
      }
      amapRef.current = AMap;
      const map = new AMap.Map(holder.current, {
        zoom: 12,
        center: [121.4737, 31.2304],
        mapStyle: "amap://styles/whitesmoke",
      });
      mapRef.current = map;
      setState("ready");
      onReady(true);
    });
    return () => {
      disposed = true;
      if (mapRef.current) {
        mapRef.current.destroy();
        mapRef.current = null;
      }
    };
    // onReady 由父组件 useCallback 稳定;建图只做一次。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 周边模式:点图取中心。
  useEffect(() => {
    const map = mapRef.current;
    if (!map || state !== "ready") return;
    const handler = (e: any) => {
      if (!aroundMode) return;
      onPickCenter({ lng: e.lnglat.getLng(), lat: e.lnglat.getLat() });
    };
    map.on("click", handler);
    return () => map.off("click", handler);
  }, [aroundMode, onPickCenter, state]);

  // 中心点 + 半径圈。
  useEffect(() => {
    const map = mapRef.current;
    const AMap = amapRef.current;
    if (!map || !AMap || state !== "ready") return;
    if (circleRef.current) {
      map.remove(circleRef.current);
      circleRef.current = null;
    }
    if (centerMarkerRef.current) {
      map.remove(centerMarkerRef.current);
      centerMarkerRef.current = null;
    }
    if (!aroundMode || !center) return;
    const pos = [center.lng, center.lat];
    centerMarkerRef.current = new AMap.CircleMarker({
      center: pos,
      radius: 7,
      fillColor: "#2563eb",
      fillOpacity: 1,
      strokeColor: "#ffffff",
      strokeWeight: 2,
      zIndex: 30,
    });
    circleRef.current = new AMap.Circle({
      center: pos,
      radius: radiusMeters,
      fillColor: "#2563eb",
      fillOpacity: 0.07,
      strokeColor: "#2563eb",
      strokeOpacity: 0.45,
      strokeWeight: 1.5,
    });
    map.add([circleRef.current, centerMarkerRef.current]);
    map.setFitView([circleRef.current], true, [40, 40, 40, 40]);
  }, [center, radiusMeters, aroundMode, state]);

  // POI Marker 渲染(items/选中变化时重画;数量≤25/页,整页重画开销可忽略)。
  useEffect(() => {
    const map = mapRef.current;
    const AMap = amapRef.current;
    if (!map || !AMap || state !== "ready") return;
    if (markersRef.current.length) {
      map.remove(markersRef.current);
      markersRef.current = [];
    }
    const located = items.filter((i) => i.longitude != null && i.latitude != null);
    const markers = located.map((i) => {
      const isSel = i.poiId === selectedPoiId;
      const hot = i.score >= 80;
      const m = new AMap.CircleMarker({
        center: [i.longitude, i.latitude],
        radius: isSel ? 10 : hot ? 7 : 5,
        fillColor: isSel ? "#1d4ed8" : hot ? "#f97316" : "#2563eb",
        fillOpacity: 1,
        strokeColor: "#ffffff",
        strokeWeight: isSel ? 3 : 1.5,
        zIndex: isSel ? 40 : hot ? 20 : 10,
        cursor: "pointer",
      });
      m.on("click", () => onSelect(i.poiId));
      return m;
    });
    markersRef.current = markers;
    if (markers.length) {
      map.add(markers);
      if (!aroundMode) map.setFitView(markers, true, [40, 40, 40, 40]);
    }
  }, [items, selectedPoiId, onSelect, aroundMode, state]);

  // 行政区联动(2026-08-24 用户拍板正式做法):选中城市/区县 → 边界描边 + setBounds;
  // 拿不到边界(偏远区县偶发)退 setZoomAndCenter。异步回来先校验焦点没变,不画过期边界。
  useEffect(() => {
    const map = mapRef.current;
    const AMap = amapRef.current;
    if (!map || !AMap || state !== "ready") return;
    if (boundaryRef.current.length) {
      map.remove(boundaryRef.current);
      boundaryRef.current = [];
    }
    if (!focus) return;
    let stale = false;
    getBoundary(focus.adcode).then((b) => {
      if (stale || !mapRef.current) return;
      if (b && b.paths.length > 0) {
        const lines = b.paths.map(
          (path: any) =>
            new AMap.Polyline({
              path,
              strokeColor: "#7c6cf0",
              strokeWeight: 2,
              strokeOpacity: 0.8,
              zIndex: 5,
            })
        );
        boundaryRef.current = lines;
        map.add(lines);
        map.setFitView(lines, true, [24, 24, 24, 24]);
      } else if (focus.center) {
        map.setZoomAndCenter(focus.level === "city" ? 11 : 13, [
          focus.center.lng,
          focus.center.lat,
        ]);
      }
    });
    return () => {
      stale = true;
    };
  }, [focus, state]);

  // 选中联动:panTo。
  useEffect(() => {
    const map = mapRef.current;
    if (!map || state !== "ready" || !selectedPoiId) return;
    const sel = items.find((i) => i.poiId === selectedPoiId);
    if (sel && sel.longitude != null && sel.latitude != null) {
      map.panTo([sel.longitude, sel.latitude]);
    }
  }, [selectedPoiId, items, state]);

  if (state === "unavailable") {
    return (
      <div className="grid h-full min-h-[320px] place-items-center rounded-lg border bg-muted/40 p-6 text-center">
        <div className="max-w-xs space-y-2">
          <MapPinOff className="mx-auto h-8 w-8 text-muted-foreground/60" aria-hidden />
          <p className="text-sm font-medium">地图暂不可用</p>
          <p className="text-xs leading-relaxed text-muted-foreground">
            高德地图 Key 未配置或加载失败。不影响搜索:结果在下方列表正常展示。
          </p>
        </div>
      </div>
    );
  }

  return <div ref={holder} className="h-full min-h-[320px] w-full rounded-lg border" />;
}
